import React, { useEffect, useState } from "react";
import { CreditCard, ArrowUpRight, X, Loader2, Flame } from "lucide-react";
import { Reveal, SectionHeading, TiltCard } from "./Atoms";
import { api } from "../../lib/api";
import { toast } from "sonner";
import PaymentBrand from "../PaymentBrand";

const EXTERNAL = [
  { id: "give-cashapp", brand: "cashapp", label: "Cash App", sub: "$RevivalFireGlobal", href: "https://cash.app/$RevivalFireGlobal" },
  { id: "give-paypal", brand: "paypal", label: "PayPal", sub: "@Revivalfireteam", href: "https://paypal.me/Revivalfireteam" },
  { id: "give-zelle", brand: "zelle", label: "Zelle", sub: "RevivalFireteam@gmail.com", href: "mailto:RevivalFireteam@gmail.com?subject=Zelle%20Giving" },
  { id: "give-bank", brand: "bank", label: "Bank Transfer", sub: "Request instructions", href: "#contact" },
];

const Give = () => {
  const [showCard, setShowCard] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public-config").then((r) => {
      setPackages(r.data.donation_packages || []);
      const def = r.data.donation_packages?.find((p) => p.amount_usd === 50);
      setSelectedPkg(def?.id || r.data.donation_packages?.[0]?.id);
    }).catch(() => {});
  }, []);

  const startCheckout = async (e) => {
    e.preventDefault();
    let payload = {
      origin_url: window.location.origin,
      donor_name: donorName.trim() || undefined,
      donor_email: donorEmail.trim() || undefined,
      note: note.trim() || undefined,
    };
    if (selectedPkg === "__custom__") {
      const amt = parseFloat(customAmount);
      if (!amt || amt < 1) { toast.error("Please enter a valid amount."); return; }
      payload.custom_amount = amt;
    } else {
      payload.package_id = selectedPkg;
    }
    setLoading(true);
    try {
      const r = await api.post("/donations/checkout", payload);
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else if (r.data?.payment_link) {
        // Backend signaled checkout unavailable but offered a Stripe Payment Link fallback
        window.open(r.data.payment_link, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (err) {
      const fallback = err?.response?.data?.payment_link;
      if (fallback) {
        window.open(fallback, "_blank", "noopener,noreferrer");
      } else {
        const msg = err?.response?.data?.detail || "Checkout failed.";
        toast.error(typeof msg === "string" ? msg : "Checkout failed.");
      }
    } finally { setLoading(false); }
  };

  const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <section id="give" className="relative py-28 px-6 section-ember overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <SectionHeading
            overline="Partner With the Fire"
            title={<>Fuel the <span className="italic gold-text">altar</span>. Send the fire.</>}
            subtitle="Your giving helps Revival Fire Ministries Global preach the Gospel, host revival gatherings, support prayer and fasting initiatives, develop Fire Hubs, train believers, reach souls, host tent revivals, crusades, and carry the fire of God into cities, regions, and nations."
          />
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* Featured card — Give by Card (Stripe) */}
          <Reveal>
            <TiltCard strength={3}>
              <button
                onClick={() => setShowCard(true)}
                className="bento-card w-full text-left flex items-start justify-between min-h-[160px] h-full !p-7 border-[#C9A44C]/40 hover:border-[#C9A44C]/70"
                data-testid="give-card"
              >
                <span className="glow-blob" />
                <div>
                  <PaymentBrand name="stripe" size={44} />
                  <h3 className="mt-4 font-serif-display text-2xl text-[#F6F0E6] leading-tight">Give by Card</h3>
                  <p className="mt-1 text-sm text-[#D9D9D9]/85">Secure card giving via Stripe</p>
                  <span className="mt-3 inline-block text-[10px] tracking-[0.24em] uppercase text-[#C9A44C]">Tap to give</span>
                </div>
                <ArrowUpRight className="h-5 w-5 text-[#C9A44C]/70" />
              </button>
            </TiltCard>
          </Reveal>

          {EXTERNAL.map(({ id, brand, label, sub, href }, i) => (
            <Reveal key={id} delay={(i + 1) * 60}>
              <TiltCard strength={3}>
                <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                  className="bento-card flex items-start justify-between min-h-[160px] h-full !p-7" data-testid={id}>
                  <span className="glow-blob" />
                  <div>
                    <PaymentBrand name={brand} size={44} />
                    <h3 className="mt-4 font-serif-display text-2xl text-[#F6F0E6] leading-tight">{label}</h3>
                    <p className="mt-1 text-sm text-[#D9D9D9]/85 break-all">{sub}</p>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-[#C9A44C]/70" />
                </a>
              </TiltCard>
            </Reveal>
          ))}
        </div>

        <Reveal delay={240}>
          <p className="mt-12 text-xs text-[#D9D9D9]/60 max-w-3xl leading-relaxed">
            Revival Fire Ministries Global uses Stripe and other secure third-party platforms.
            No card data is stored on this website.
          </p>
        </Reveal>
      </div>

      {/* Stripe modal */}
      {showCard && (
        <div className="fixed inset-0 z-50 bg-[#050505]/85 backdrop-blur-md flex items-center justify-center px-6 py-10 overflow-y-auto" onClick={() => setShowCard(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={startCheckout} className="bento-card !p-8 max-w-lg w-full space-y-5" data-testid="give-card-modal">
            <span className="glow-blob" />
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">Give by Card</div>
                <h2 className="font-serif-display text-2xl text-[#F6F0E6] mt-1">Partner with the fire</h2>
              </div>
              <button type="button" onClick={() => setShowCard(false)} className="h-9 w-9 rounded-full border border-[#C9A44C]/30 inline-flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>

            <div>
              <label className="field-label">Choose an amount</label>
              <div className="grid grid-cols-3 gap-2.5 mt-2">
                {packages.map((p) => (
                  <button key={p.id} type="button" onClick={() => setSelectedPkg(p.id)}
                    className={`py-3 rounded-xl border text-sm font-medium transition-all ${selectedPkg === p.id ? "border-[#C9A44C] bg-[#C9A44C]/10 text-[#F6F0E6]" : "border-[#C9A44C]/25 text-[#D9D9D9] hover:border-[#C9A44C]/50"}`}
                    data-testid={`amount-${p.id}`}>
                    {fmt(p.amount_usd)}
                  </button>
                ))}
                <button type="button" onClick={() => setSelectedPkg("__custom__")}
                  className={`py-3 rounded-xl border text-sm font-medium ${selectedPkg === "__custom__" ? "border-[#C9A44C] bg-[#C9A44C]/10 text-[#F6F0E6]" : "border-[#C9A44C]/25 text-[#D9D9D9]"}`}
                  data-testid="amount-custom">
                  Custom
                </button>
              </div>
              {selectedPkg === "__custom__" && (
                <div className="mt-3">
                  <input type="number" step="0.01" min="1" max="25000" className="input-fire" placeholder="Enter amount in USD" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} data-testid="custom-amount-input" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="field-label">Name <span className="opacity-60 normal-case tracking-normal">(optional)</span></label>
                <input className="input-fire" value={donorName} onChange={(e) => setDonorName(e.target.value)} data-testid="donor-name" />
              </div>
              <div>
                <label className="field-label">Email <span className="opacity-60 normal-case tracking-normal">(optional)</span></label>
                <input type="email" className="input-fire" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} data-testid="donor-email" />
              </div>
            </div>
            <div>
              <label className="field-label">Note <span className="opacity-60 normal-case tracking-normal">(optional)</span></label>
              <input className="input-fire" placeholder="e.g. seed for tent revival" value={note} onChange={(e) => setNote(e.target.value)} data-testid="donor-note" />
            </div>

            <button type="submit" disabled={loading} className="btn-fire w-full" data-testid="checkout-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flame className="h-4 w-4" />}
              {loading ? "Redirecting to Stripe…" : "Continue to Secure Checkout"}
            </button>
            <p className="text-[11px] text-[#D9D9D9]/55 text-center">Powered by Stripe · Your card details are never sent to RFMG.</p>
          </form>
        </div>
      )}
    </section>
  );
};

export default Give;
